import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaCheckCircle,
  FaSpinner,
  FaExclamationTriangle,
} from "react-icons/fa";
import { verifyPayment } from "../api/payment";
import { trackEvent } from "../utils/metaPixel";
import { trackButtonClick } from "../utils/analytics";

const PaymentSuccess = () => {
  const [verificationStatus, setVerificationStatus] = useState("verifying"); // verifying, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Enhanced verification with retry and user plan check
  const verifyPaymentWithRetry = useCallback(async (params, retryCount = 0) => {
    const maxRetries = 3;
    const retryDelay = 2000; // 2 seconds

    try {
      console.log(
        `🔍 Payment verification attempt ${retryCount + 1}/${maxRetries + 1}`,
        params
      );

      // Verify payment with our API
      const verificationParams = {};
      if (params.orderCode) verificationParams.orderCode = params.orderCode;
      if (params.paymentLinkId)
        verificationParams.paymentLinkId = params.paymentLinkId;
      if (params.orderId) verificationParams.orderId = params.orderId;

      console.log(
        "📨 Sending verification request with params:",
        verificationParams
      );

      const verificationResult = await verifyPayment(verificationParams);

      console.log("✅ Payment verification result received:", {
        success: verificationResult.success,
        status: verificationResult.data?.status,
        verified: verificationResult.verified,
        message: verificationResult.message,
        hasData: !!verificationResult.data,
      });

      // Check if verification was successful
      if (verificationResult.success) {
        const paymentStatus = verificationResult.data?.status;
        // Use server-provided verified flag if available, otherwise fallback to status === 'PAID'
        const verified =
          verificationResult.verified ?? paymentStatus === "PAID";
        console.log(
          `📊 Payment status check: ${paymentStatus}, verified: ${verified}`
        );

        if (verified) {
          // Success - payment is verified
          console.log(
            `🎉 Payment verified successfully! Status: ${paymentStatus}`
          );

          setVerificationStatus("success");
          setPaymentData(verificationResult.data);
          await cleanupAfterSuccess(verificationResult.data, params.orderCode);
          return; // Exit early on success
        } else if (paymentStatus === "PENDING") {
          // Payment is still pending
          console.log("⏳ Payment is still PENDING, may need to wait...");

          if (retryCount < maxRetries) {
            console.log(
              `🔄 Retrying verification for PENDING payment in ${retryDelay}ms...`
            );
            setTimeout(() => {
              verifyPaymentWithRetry(params, retryCount + 1).catch(
                (retryError) => {
                  console.error("PENDING retry failed:", retryError);
                  setVerificationStatus("failed");
                  setError(
                    `Payment is still pending after ${maxRetries} retries. Please check your payment status.`
                  );
                }
              );
            }, retryDelay);
            return; // Exit to wait for retry
          } else {
            // Max retries reached for pending payment
            setVerificationStatus("failed");
            setError(
              "Payment verification timed out. Payment may still be processing."
            );
            return;
          }
        } else {
          // Other status (CANCELLED, FAILED, etc.)
          const errorMsg = `Payment not successful. Status: ${
            paymentStatus || "unknown"
          }`;
          console.warn("⚠️ Payment verification failed:", errorMsg);
          throw new Error(errorMsg);
        }
      } else {
        // API call failed
        const errorMsg =
          verificationResult.message || "Payment verification API failed";
        console.error("❌ API verification failed:", errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error(`❌ Verification attempt ${retryCount + 1} failed:`, {
        error: error.message,
        stack: error.stack,
        params: params,
        retryCount: retryCount,
      });

      if (retryCount < maxRetries) {
        console.log(
          `⏳ Verification failed, retrying in ${retryDelay}ms... (${
            retryCount + 1
          }/${maxRetries})`
        );

        setTimeout(() => {
          verifyPaymentWithRetry(params, retryCount + 1).catch((retryError) => {
            console.error("Final retry failed:", retryError);
            setVerificationStatus("failed");
            setError(
              retryError.message ||
                "Payment verification failed after all retries"
            );
          });
        }, retryDelay);
      } else {
        // Final failure after all retries
        console.error("🚫 All verification attempts failed");
        setVerificationStatus("failed");
        setError(
          error.message || "Payment verification failed after all retries"
        );
      }
    }
  }, []);

  useEffect(() => {
    const handlePaymentVerification = async () => {
      try {
        // Get query parameters from URL
        const urlParams = new URLSearchParams(location.search);
        const orderCode = urlParams.get("orderCode");
        const paymentLinkId = urlParams.get("paymentLinkId");
        const orderId = urlParams.get("orderId");

        console.log("🔍 Payment verification params:", {
          orderCode,
          paymentLinkId,
          orderId,
          fullUrl: window.location.href,
        });

        // Track payment success page visit
        trackEvent("PaymentSuccessPageView", {
          orderCode,
          paymentLinkId,
          source: "payos_redirect",
        });

        // Enhanced verification with retry mechanism
        await verifyPaymentWithRetry({ orderCode, paymentLinkId, orderId });
      } catch (error) {
        console.error("❌ Payment verification error:", error);
        setVerificationStatus("failed");
        setError(error.message);

        // Track payment verification failure
        trackEvent("PaymentVerificationFailed", {
          error: error.message,
          source: "payos_redirect",
        });
      }
    };

    handlePaymentVerification();
  }, [location, verifyPaymentWithRetry]);

  // Helper function to cleanup after successful verification
  const cleanupAfterSuccess = async (paymentData, orderCode) => {
    // Track successful payment
    trackEvent("PaymentSuccess", {
      amount: paymentData?.amount,
      orderCode: orderCode,
      paymentMethod: "PayOS",
    });

    // BACKUP: Update user plan directly when webhook fails
    try {
      console.log("🔄 Attempting to update user plan as webhook backup...");

      const userId = localStorage.getItem("userId");
      if (userId && paymentData) {
        // Get stored payment data to update plan
        const latestPaymentData = localStorage.getItem("latestPaymentData");
        if (latestPaymentData) {
          const storedPayment = JSON.parse(latestPaymentData);

          // Call our server API to update user plan directly
          const updateResult = await fetch(
            `${
              process.env.REACT_APP_API_URL || "http://localhost:3005"
            }/api/payment/force-update-plan`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                userId,
                orderCode,
                paymentLinkId: paymentData.paymentLinkId,
                planId: storedPayment.planId,
                planName: storedPayment.planName,
                amount: storedPayment.amount,
                source: "frontend_backup",
              }),
            }
          );

          if (updateResult.ok) {
            const updateResponse = await updateResult.json();
            console.log(
              "✅ User plan updated via backup mechanism:",
              updateResponse
            );

            // Update localStorage userData
            const userData = localStorage.getItem("userData");
            if (userData) {
              const user = JSON.parse(userData);
              user.membership = storedPayment.planId;
              user.plan = {
                id: storedPayment.planId,
                name: storedPayment.planName,
                status: "PAID",
                activatedAt: new Date().toISOString(),
              };
              localStorage.setItem("userData", JSON.stringify(user));
              console.log("✅ Updated userData in localStorage");
            }
          } else {
            console.warn("⚠️ Failed to update user plan via backup");
          }
        }
      }
    } catch (backupError) {
      console.error("❌ Backup plan update failed:", backupError);
    }

    // Remove pending payment data from localStorage
    try {
      localStorage.removeItem("latestPaymentData");
      console.log("🧹 Cleaned up payment data from localStorage");
    } catch (e) {
      console.warn("Could not remove payment data from localStorage:", e);
    }
  };

  const handleContinue = () => {
    trackButtonClick("continue_after_payment_success");
    navigate("/");
  };

  const handleViewPricing = () => {
    trackButtonClick("view_pricing_after_payment");
    navigate("/pricing");
  };

  const renderVerificationStatus = () => {
    switch (verificationStatus) {
      case "verifying":
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
              <FaSpinner className="text-blue-600 text-2xl animate-spin" />
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Đang xác minh thanh toán...
            </h1>
            <p className="text-gray-600 text-lg">
              Vui lòng chờ trong giây lát để chúng tôi xác nhận thanh toán của
              bạn.
            </p>
          </div>
        );

      case "success":
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
              <FaCheckCircle className="text-green-600 text-2xl" />
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Thanh toán thành công! 🎉
            </h1>
            <p className="text-gray-600 text-lg mb-8">
              Cảm ơn bạn đã tin tương và sử dụng dịch vụ AIPrice. Gói dịch vụ đã
              được kích hoạt thành công!
            </p>

            {paymentData && (
              <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left max-w-md mx-auto">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Thông tin thanh toán:
                </h3>
                <div className="space-y-2 text-sm">
                  {paymentData.amount && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Số tiền:</span>
                      <span className="font-medium">
                        {new Intl.NumberFormat("vi-VN").format(
                          paymentData.amount
                        )}{" "}
                        ₫
                      </span>
                    </div>
                  )}
                  {paymentData.orderCode && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mã đơn hàng:</span>
                      <span className="font-medium">
                        {paymentData.orderCode}
                      </span>
                    </div>
                  )}
                  {paymentData.transactionDateTime && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Thời gian:</span>
                      <span className="font-medium">
                        {new Date(
                          paymentData.transactionDateTime
                        ).toLocaleString("vi-VN")}
                      </span>
                    </div>
                  )}
                  {paymentData.counterAccountBankName && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ngân hàng:</span>
                      <span className="font-medium">
                        {paymentData.counterAccountBankName}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleContinue}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
              >
                Tiếp tục sử dụng dịch vụ
              </button>
              <button
                onClick={handleViewPricing}
                className="px-8 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200"
              >
                Xem các gói khác
              </button>
            </div>
          </div>
        );

      case "failed":
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
              <FaExclamationTriangle className="text-red-600 text-2xl" />
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Không thể xác minh thanh toán
            </h1>
            <p className="text-gray-600 text-lg mb-4">
              Chúng tôi không thể xác minh trạng thái thanh toán của bạn.
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
            <p className="text-gray-600 text-sm mb-8">
              Nếu bạn đã thanh toán thành công, vui lòng liên hệ với chúng tôi
              để được hỗ trợ.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
              >
                Thử lại
              </button>
              <button
                onClick={() => navigate("/pricing")}
                className="px-8 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200"
              >
                Quay lại
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
          {renderVerificationStatus()}
        </div>

        {/* Additional Information */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            Cần hỗ trợ? Liên hệ với chúng tôi qua email{" "}
            <a
              href="mailto:contact@youhomes.vn"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              contact@youhomes.vn
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
